import { useState } from 'react'
import { formatDistanceToNow, format } from 'date-fns'
import { nb } from 'date-fns/locale'
import type { Collection as CollectionType } from '../../lib/types'
import type { SignedUrlVariants } from '../../lib/api'
import Item from './Item'

interface CollectionProps {
  collection: CollectionType
  onItemClick: (itemId: string) => void
  editMode: boolean
  onDeleteItem: (itemId: string, collectionId: string, storagePath?: string) => Promise<void>
  onDeleteCollection: (collectionId: string) => Promise<void>
  deletingItemIds: Set<string>
  signedUrls: Map<string, SignedUrlVariants>
}

export default function Collection({
  collection,
  onItemClick,
  editMode,
  onDeleteItem,
  onDeleteCollection,
  deletingItemIds,
  signedUrls,
}: CollectionProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showExactDate, setShowExactDate] = useState(false)

  const displayDate = new Date(collection.date ?? collection.createdAt)
  const timeAgo = formatDistanceToNow(displayDate, { addSuffix: true, locale: nb })
  const exactDate = format(displayDate, 'd. MMM yyyy', { locale: nb })

  // Items are ordered by user-defined position (set at upload time).
  const sortedItems = [...collection.items].sort((a, b) => a.position - b.position)

  const itemCount = sortedItems.length

  function getGridColumns(count: number): string {
    if (count === 1) return 'grid grid-cols-1 gap-1'
    if (count === 2 || count === 4) return 'grid grid-cols-2 gap-1'
    return 'grid grid-cols-3 gap-1'
  }

  const itemGridClass = getGridColumns(itemCount)

  const handleDeleteCollection = async () => {
    setDeleting(true)
    try {
      await onDeleteCollection(collection.id)
    } finally {
      setDeleting(false)
      setConfirmingDelete(false)
    }
  }

  return (
    <div className="col-span-full">
      {/* Collection header */}
      <div className="pb-5 relative flex items-start justify-between gap-3 px-4">
        <div className="min-w-0">
          {collection.title ? (
            <h3 className="text-lg font-light text-white leading-tight truncate">
              {collection.title}
            </h3>
          ) : (
            <h3 className="text-lg font-light text-white/60 leading-tight">
              Uten tittel
            </h3>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right text-sm font-light text-white/70">
            <button
              onClick={() => setShowExactDate((v) => !v)}
              className="hover:text-white/80 transition-colors"
            >
              {showExactDate ? exactDate : timeAgo}
            </button>
            {' • '}
            <span>{collection.username}</span>
          </div>
          {editMode && (
          confirmingDelete ? (
            <div className="flex items-center gap-1">
              <button
                onClick={handleDeleteCollection}
                disabled={deleting}
                className="text-xs font-medium text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded-full transition-colors disabled:opacity-50"
              >
                {deleting ? '...' : 'Slett'}
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="text-xs font-medium text-white/60 bg-white/10 hover:bg-white/20 px-2 py-1 rounded-full transition-colors"
              >
                Avbryt
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full"
              aria-label="Slett samling"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
          )
        )}
        </div>
      </div>

      {/* Items grid */}
      <div className={`${itemGridClass}`}>
        {sortedItems.map((item) => (
          <Item
            key={item.id}
            item={item}
            onClick={() => onItemClick(item.id)}
            editMode={editMode}
            onDelete={() => onDeleteItem(item.id, collection.id, item.storagePath)}
            isDeleting={deletingItemIds.has(item.id)}
            signedUrl={signedUrls.get(item.id)?.thumb}

          />
        ))}
      </div>
    </div>
  )
}
